

import { useState, useEffect, useMemo } from "react";
import {
  Search,
  User,
  Users,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Filter,
  Calendar,
  Clock,
  IndianRupee,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import { mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrency, formatDateTime, formatDate } from "@/lib/data";

interface PaymentLog {
  id: number;
  user_id: number;
  payment_type: 'client' | 'vendor';
  payment_id: number;
  action: string;
  amount: number;
  currency: string;
  payment_method: string | null;
  reference_number: string | null;
  client_id: number | null;
  vendor_id: number | null;
  event_id: number | null;
  description: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  user?: { name: string; email: string };
  client?: { name: string };
  vendor?: { name: string };
  event?: { name: string };
}

interface Stats {
  total: number;
  today: number;
  client_payments: { count: number; amount: number };
  vendor_payments: { count: number; amount: number };
}

function StatBadge({ label, value, color, subvalue }: { label: string; value: string | number; color: string; subvalue?: string }) {
  return (
    <div className={`px-3 py-2 rounded-lg ${color} flex flex-col`}>
      <span className="text-xs font-medium opacity-80">{label}</span>
      <span className="text-sm font-bold">{value}</span>
      {subvalue && <span className="text-xs opacity-70">{subvalue}</span>}
    </div>
  );
}

function getActionIcon(action: string) {
  const actionLower = action.toLowerCase();
  if (actionLower === 'created') return ArrowDownLeft;
  if (actionLower === 'deleted') return ArrowUpRight;
  if (actionLower === 'updated') return CreditCard;
  return Wallet;
}

function getActionColor(action: string) {
  const actionLower = action.toLowerCase();
  if (actionLower === 'created') return 'bg-emerald-100 text-emerald-700';
  if (actionLower === 'deleted') return 'bg-red-100 text-red-700';
  if (actionLower === 'updated') return 'bg-blue-100 text-blue-700';
  return 'bg-stone-100 text-stone-700';
}

export default function PaymentLogsPage() {
  const { showToast } = useToast();

  const [logs, setLogs] = useState<PaymentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // The search term the list was last loaded with (the input only applies on submit)
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [stats, setStats] = useState<Stats>({ 
    total: 0, 
    today: 0, 
    client_payments: { count: 0, amount: 0 }, 
    vendor_payments: { count: 0, amount: 0 } 
  });

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadData();
  }, [currentPage, selectedType, selectedAction, fromDate, toDate, appliedSearch]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage };
      if (selectedType) params.payment_type = selectedType;
      if (selectedAction) params.action = selectedAction;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (appliedSearch) params.search = appliedSearch;
      
      const response = await mastersApi.paymentLogs.list(params);
      const data = response.data?.data;
      setLogs(data?.data || []);
      setLastPage(data?.last_page || 1);
      setTotal(data?.total || 0);
    } catch (error) {
      showToast("Failed to load payment logs", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await mastersApi.paymentLogs.stats();
      setStats(response.data?.data || { total: 0, today: 0, client_payments: { count: 0, amount: 0 }, vendor_payments: { count: 0, amount: 0 } });
    } catch (error) {
      // Silent fail
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedSearch(searchQuery.trim());
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedType("");
    setSelectedAction("");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    setAppliedSearch("");
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  const filteredLogs = useMemo(() => {
    if (!searchQuery) return logs;
    const q = searchQuery.toLowerCase();
    return logs.filter(
      (log) =>
        log.action.toLowerCase().includes(q) ||
        log.reference_number?.toLowerCase().includes(q) ||
        log.client?.name?.toLowerCase().includes(q) ||
        log.vendor?.name?.toLowerCase().includes(q) ||
        log.payment_method?.toLowerCase().includes(q)
    );
  }, [logs, searchQuery]);

  if (loading && logs.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-stone-800">Payment Logs</h1>
        <p className="text-sm text-stone-500">Track all payment transactions and financial activities.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <StatBadge 
          label="Total" 
          value={stats.total} 
          color="bg-stone-100 text-stone-700" 
        />
        <StatBadge 
          label="Today" 
          value={stats.today} 
          color="bg-blue-100 text-blue-700" 
        />
        <StatBadge 
          label="Client Payments" 
          value={stats.client_payments.count} 
          color="bg-emerald-100 text-emerald-700" 
          subvalue={formatCurrency(stats.client_payments.amount)}
        />
        <StatBadge 
          label="Vendor Payments" 
          value={stats.vendor_payments.count} 
          color="bg-amber-100 text-amber-700" 
          subvalue={formatCurrency(stats.vendor_payments.amount)}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-stone-200 p-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <div className="w-full sm:flex-1 sm:min-w-[220px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search payments..."
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>
          <select
            value={selectedType}
            onChange={(e) => { setSelectedType(e.target.value); setCurrentPage(1); }}
            className="w-full sm:w-auto px-3 py-2 rounded-xl border border-stone-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          >
            <option value="">All Types</option>
            <option value="client">Client Payments</option>
            <option value="vendor">Vendor Payments</option>
          </select>
          <select
            value={selectedAction}
            onChange={(e) => { setSelectedAction(e.target.value); setCurrentPage(1); }}
            className="w-full sm:w-auto px-3 py-2 rounded-xl border border-stone-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          >
            <option value="">All Actions</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="deleted">Deleted</option>
          </select>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full min-w-0 sm:w-auto px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              aria-label="From date"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full min-w-0 sm:w-auto px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              aria-label="To date"
            />
          </div>
          <button
            onClick={clearFilters}
            className="px-4 py-2 min-h-10 rounded-xl bg-stone-100 text-stone-600 font-medium text-sm hover:bg-stone-200 transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {filteredLogs.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Wallet className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No payment logs found</p>
        </div>
      ) : (
        <>
          {/* Mobile Card Layout */}
          <div className="md:hidden space-y-2">
            {filteredLogs.map((log) => {
              const ActionIcon = getActionIcon(log.action);
              const isClient = log.payment_type === 'client';
              return (
                <div key={log.id} className="bg-white rounded-xl border border-stone-200 p-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getActionColor(log.action)}`}>
                      <ActionIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isClient ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {isClient ? 'Client' : 'Vendor'}
                        </span>
                        <span className="text-xs text-stone-400">{formatTimeAgo(log.created_at)}</span>
                      </div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-stone-800">{formatCurrency(log.amount)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </div>
                      <div className="text-xs text-stone-500 mb-1">
                        {isClient ? log.client?.name : log.vendor?.name}
                        {log.event?.name ? ` · ${log.event.name}` : ""}
                      </div>
                      <div className="text-[11px] text-stone-400 mb-1">By {log.user?.name || "System"}</div>
                      <div className="flex items-center gap-2 text-xs text-stone-400 min-w-0">
                        <CreditCard className="w-3 h-3 shrink-0" />
                        <span className="shrink-0">{log.payment_method || 'N/A'}</span>
                        {log.reference_number && <span className="truncate">· {log.reference_number}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden md:block bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Type</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Action</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500">Amount</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Party</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Method</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Reference</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredLogs.map((log) => {
                    const ActionIcon = getActionIcon(log.action);
                    const isClient = log.payment_type === 'client';
                    return (
                      <tr key={log.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isClient ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {isClient ? 'Client' : 'Vendor'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded flex items-center justify-center ${getActionColor(log.action)}`}>
                              <ActionIcon className="w-3 h-3" />
                            </div>
                            <span className="text-xs capitalize">{log.action}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="font-semibold text-stone-800">{formatCurrency(log.amount)}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {isClient ? (
                              <User className="w-4 h-4 text-stone-400" />
                            ) : (
                              <Users className="w-4 h-4 text-stone-400" />
                            )}
                            <span className="text-sm text-stone-600">
                              {isClient ? log.client?.name : log.vendor?.name}
                              {log.event?.name && <span className="block text-xs text-stone-400">{log.event.name}</span>}
                              <span className="block text-[11px] text-stone-400">By {log.user?.name || "System"}</span>
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-sm text-stone-600 capitalize">
                          {log.payment_method || '-'}
                        </td>
                        <td className="px-3 py-2 text-xs text-stone-500">
                          {log.reference_number || '-'}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm text-stone-600">{formatDateTime(log.created_at)}</div>
                          <div className="text-xs text-stone-400">{formatTimeAgo(log.created_at)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {lastPage > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2 mt-4 px-2">
              <div className="text-sm text-stone-500">
                Page {currentPage} of {lastPage}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  className="h-10 min-w-10 md:h-auto md:min-w-0 flex items-center justify-center px-3 py-1.5 rounded-lg bg-stone-100 text-stone-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                  disabled={currentPage === lastPage}
                  aria-label="Next page"
                  className="h-10 min-w-10 md:h-auto md:min-w-0 flex items-center justify-center px-3 py-1.5 rounded-lg bg-stone-100 text-stone-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}
